import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import parse from 'html-react-parser';

const ProductGuideOverlay = (props) => {
  const {
    title,
    downloadUrl,
    downloadLabel,
    addToBasketLabel,
    addToBasketColumnLabel,
    skuTerms,
    isDansac,
    label,
    data,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [count, setCount] = useState(1);
  const { TableAttributes, Skus } = data;
  const [originalSkus] = useState([...data.Skus]);
  const [Items, setItems] = useState(Skus);
  const [isDesktop, setIsDesktop] = useState(true);
  const [colWidth, setColWidth] = useState(0);
  const tableRef = useRef(null);
  const cols = TableAttributes.length + 1;
  const setWidth = isDesktop
    ? { minWidth: `${colWidth}px`, maxWidth: `${colWidth}px` }
    : null;
  const handleSample = () => Items.some((item) => item['CanSample']);

  const calculateColWidth = () => {
    if (tableRef.current) {
      const tableWidth = tableRef.current.offsetWidth;
      const colWidth = tableWidth / (cols - (handleSample() ? 0 : 1));
      setColWidth(colWidth);
    }
  };

  useEffect(() => {
    tableRef.current && isOpen && calculateColWidth();
  }, [isOpen]);

  const handleResize = () => {
    calculateColWidth();
    if (window.matchMedia('(min-width: 1024px)').matches) {
      setIsDesktop(true);
    } else {
      setIsDesktop(false);
    }
  };

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleSort = (attribute) => {
    const cloneItems = [...Items];
    if (attribute === 'ID') {
      return;
    }

    if (count === 3) {
      setCount(1);
      setItems(originalSkus);
      return;
    }

    const sortedSKUS = cloneItems.sort((a, b) => {
      if (a[attribute] < b[attribute]) {
        return -1;
      }
      if (a[attribute] > b[attribute]) {
        return 1;
      }
      return 0;
    });
    // count number of clicks on attribute
    setCount(count + 1);

    // if count is odd, sort ascending, if count is even, sort descending
    if (count % 2 === 0) {
      sortedSKUS.reverse();
    }

    setItems(sortedSKUS);
  };

  return (
    <>
      <button
        className="product-detail__footer-button"
        type="button"
        onClick={() => setIsOpen(true)}
      >
        {parse(label)}
      </button>

      {isOpen ? (
        <dialog
          className={
            isDansac
              ? 'product-detail__guide-overlay product-detail__guide-overlay--dansac'
              : 'product-detail__guide-overlay'
          }
          open
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsOpen(false);
            }
          }}
        >
          <div className="product-detail__guide-overlay__inner">
            <button
              className="product-detail__guide-overlay__close-button"
              onClick={() => setIsOpen(false)}
            >
              <svg role="presentation">
                <use xlinkHref="#modal-close"></use>
              </svg>
            </button>
            <div className="product-detail__guide-overlay__section">
              <div className="product-detail__guide-overlay-header">
                <h2 className="product-detail__guide-overlay__title">
                  {parse(title)}
                </h2>
                <a href={downloadUrl} className="btn btn--primary" download>
                  {parse(downloadLabel)}
                  <svg role="presentation">
                    <use xlinkHref="#download-icon"></use>
                  </svg>
                </a>
              </div>
              <table
                className="product-detail__guide-overlay__table desktop"
                ref={tableRef}
              >
                <thead className="product-detail__guide-overlay__thead">
                  <tr>
                    {TableAttributes.map((attribute, i) => (
                      <th
                        className="product-detail__guide-overlay__thead-cell"
                        key={i}
                        onClick={() => handleSort(attribute.SKUPropertyName)}
                        style={setWidth}
                      >
                        <span>
                          {parse(attribute.Label)}
                          <svg role="presentation">
                            <use xlinkHref="#toggle-icon"></use>
                          </svg>
                        </span>
                      </th>
                    ))}
                    {handleSample() && (
                      <th style={setWidth}>{parse(addToBasketColumnLabel)}</th>
                    )}
                  </tr>
                </thead>
                <tbody className="product-detail__guide-overlay__tbody">
                  {Items.length
                    ? Items.map((item, i) => (
                      <tr
                        key={i}
                        className="product-detail__guide-overlay__row"
                      >
                        {TableAttributes.length &&
                            TableAttributes.map((att, j) => {
                              if (
                                item[att.SKUPropertyName] &&
                                item[att.SKUPropertyName].includes('color_dot')
                              ) {
                                const parsedContent = parse(
                                  item[att.SKUPropertyName]
                                );
                                let string = '',
                                  spanElement = null;
                                parsedContent.forEach(element => {
                                  if (typeof element === 'string') {
                                    string = element;
                                  } else if (React.isValidElement(element)) {
                                    spanElement = element;
                                  }
                                });
                                const colorStyle =
                                  spanElement.props.style.color || '';
                                if (colorStyle && string) {
                                  return (
                                    <td
                                      key={j}
                                      className="product-detail__column"
                                      data-label={att.Label}
                                      style={setWidth}
                                    >
                                      <div className="color-container">
                                        <span
                                          className="color-dot"
                                          style={{ color: colorStyle }}
                                        >
                                          •
                                        </span>
                                        {string}
                                      </div>
                                    </td>
                                  );
                                }
                              }

                              return item[att.SKUPropertyName] ? (
                                <td
                                  key={j}
                                  className="product-detail__column"
                                  data-label={att.Label}
                                  style={setWidth}
                                >
                                  {parse(item[att.SKUPropertyName])}
                                </td>
                              ) : (
                                <td
                                  key={j}
                                  className="product-detail__column"
                                  data-label=""
                                  style={setWidth}
                                ></td>
                              );
                            })}

                        {handleSample() &&
                            Object.keys(item).map((skuKey, i) => {
                              if (skuKey === 'ID') {
                                return (
                                  <td
                                    key={i}
                                    className="product-detail__column product-detail__column-full"
                                    style={setWidth}
                                  >
                                    {item.CanSample && (
                                      <button
                                        className="product-detail__button"
                                        data-id={item[skuKey]}
                                        onClick={() => {
                                          window.addProduct(item[skuKey]);
                                          setIsOpen(false);
                                        }}
                                      >
                                        {parse(addToBasketLabel)}
                                      </button>
                                    )}
                                  </td>
                                );
                              }
                            })}
                      </tr>
                    ))
                    : null}
                </tbody>
              </table>
              {skuTerms ? (
                <div className="product-detail__guide-overlay__section">
                  <p className="product-detail__guide-overlay__terms">
                    {parse(skuTerms)}
                  </p>
                </div>
              ) : (
                ''
              )}
            </div>
          </div>
        </dialog>
      ) : null}
    </>
  );
};

ProductGuideOverlay.propTypes = {
  title: PropTypes.string.isRequired,
  downloadUrl: PropTypes.string.isRequired,
  downloadLabel: PropTypes.string.isRequired,
  addToBasketLabel: PropTypes.string.isRequired,
  addToBasketColumnLabel: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
};

export default ProductGuideOverlay;
